import React, { useState, useEffect } from 'react';
import { useTranslation } from '../context/LanguageContext';
import { apiService } from '../services/api';
import { 
  GlobalTimeslotDefinitionResponse, 
  CreateGlobalTimeslotRequest,
  UpdateGlobalTimeslotTimeRequest,
  UpdateGlobalTimeslotCapacityRequest,
  UpdateGlobalTimeslotEnabledRequest,
  DayOfWeek 
} from '../types/api';
import './GlobalTimeslotManagement.css';

interface GlobalTimeslotManagementProps {
  onUpdate?: () => void;
}

const GlobalTimeslotManagement: React.FC<GlobalTimeslotManagementProps> = ({ onUpdate }) => {
  const { t } = useTranslation();
  const [timeslots, setTimeslots] = useState<GlobalTimeslotDefinitionResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [selectedDay, setSelectedDay] = useState<DayOfWeek>(DayOfWeek.Monday);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTimeslot, setEditingTimeslot] = useState<GlobalTimeslotDefinitionResponse | null>(null);
  const [editMode, setEditMode] = useState<'time' | 'capacity' | null>(null);

  // Form states
  const [createForm, setCreateForm] = useState<CreateGlobalTimeslotRequest>({
    startTime: '',
    endTime: '',
    capacity: 1,
    dayOfWeek: DayOfWeek.Monday,
    reason: ''
  });

  const [editForm, setEditForm] = useState({
    newStartTime: '',
    newEndTime: '',
    newCapacity: 1,
    reason: ''
  });

  // Copy & Paste functionality
  const [copiedTimeslots, setCopiedTimeslots] = useState<GlobalTimeslotDefinitionResponse[]>([]);
  const [copiedFromDay, setCopiedFromDay] = useState<string>('');
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteTargetDay, setPasteTargetDay] = useState<DayOfWeek>(DayOfWeek.Monday);
  const [pasteError, setPasteError] = useState<string>('');
  const [pasteLoading, setPasteLoading] = useState(false);

  const dayNames = {
    [DayOfWeek.Monday]: t('monday'),
    [DayOfWeek.Tuesday]: t('tuesday'),
    [DayOfWeek.Wednesday]: t('wednesday'),
    [DayOfWeek.Thursday]: t('thursday'),
    [DayOfWeek.Friday]: t('friday'),
    [DayOfWeek.Saturday]: t('saturday'),
    [DayOfWeek.Sunday]: t('sunday')
  };

  // Order days with Monday first and Sunday last
  const orderedDays = [
    DayOfWeek.Monday,
    DayOfWeek.Tuesday,
    DayOfWeek.Wednesday,
    DayOfWeek.Thursday,
    DayOfWeek.Friday,
    DayOfWeek.Saturday,
    DayOfWeek.Sunday
  ];

  useEffect(() => {
    loadTimeslots();
  }, [selectedDay]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTimeslots = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await apiService.getGlobalTimeslots(selectedDay);
      
      if (response.success && response.data) {
        setTimeslots(response.data);
      } else {
        setError(response.message || 'Failed to load timeslots');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load timeslots');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimeSpan = (timeSpan: any): string => {
    if (!timeSpan) return '';
    
    // If it's already a string (HH:MM:SS format), extract HH:MM
    if (typeof timeSpan === 'string') {
      return timeSpan.substring(0, 5); // Extract "HH:MM" from "HH:MM:SS"
    }
    
    // If it's a TimeSpan object (backward compatibility)
    if (typeof timeSpan === 'object' && timeSpan.totalHours !== undefined) {
      const hours = Math.floor(timeSpan.totalHours);
      const minutes = timeSpan.minutes;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
    
    return '';
  };

  const handleCreateTimeslot = async () => {
    try {
      const response = await apiService.createGlobalTimeslot(createForm);
      if (response.success) {
        setShowCreateModal(false);
        setCreateForm({
          startTime: '',
          endTime: '',
          capacity: 1,
          dayOfWeek: DayOfWeek.Monday,
          reason: ''
        });
        await loadTimeslots();
        onUpdate?.();
      } else {
        setError(response.message || 'Failed to create timeslot');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create timeslot');
    }
  };

  const handleEditTimeslot = async () => {
    if (!editingTimeslot) return;

    try {
      const originalStart = formatTimeSpan(editingTimeslot.originalStartTime);
      const originalEnd = formatTimeSpan(editingTimeslot.originalEndTime);

      let response;
      if (editMode === 'time') {
        const request: UpdateGlobalTimeslotTimeRequest = {
          newStartTime: editForm.newStartTime,
          newEndTime: editForm.newEndTime,
          dayOfWeek: editingTimeslot.dayOfWeek,
          reason: editForm.reason
        };
        response = await apiService.updateGlobalTimeslotTime(originalStart, originalEnd, request);
      } else if (editMode === 'capacity') {
        const request: UpdateGlobalTimeslotCapacityRequest = {
          newCapacity: editForm.newCapacity,
          dayOfWeek: editingTimeslot.dayOfWeek,
          reason: editForm.reason
        };
        response = await apiService.updateGlobalTimeslotCapacity(originalStart, originalEnd, request);
      }

      if (response?.success) {
        setEditingTimeslot(null);
        setEditMode(null);
        setEditForm({
          newStartTime: '',
          newEndTime: '',
          newCapacity: 1,
          reason: ''
        });
        await loadTimeslots();
        onUpdate?.();
      } else {
        setError(response?.message || 'Failed to update timeslot');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update timeslot');
    }
  };

  const handleToggleTimeslot = async (timeslot: GlobalTimeslotDefinitionResponse) => {
    try {
      const originalStart = formatTimeSpan(timeslot.originalStartTime);
      const originalEnd = formatTimeSpan(timeslot.originalEndTime);
      
      const request: UpdateGlobalTimeslotEnabledRequest = {
        isEnabled: !timeslot.isEnabled,
        dayOfWeek: timeslot.dayOfWeek,
        reason: `${timeslot.isEnabled ? 'Disabled' : 'Enabled'} timeslot`
      };
      
      const response = await apiService.updateGlobalTimeslotEnabled(originalStart, originalEnd, request);
      if (response?.success) {
        await loadTimeslots();
        onUpdate?.();
      } else {
        setError(response?.message || 'Failed to toggle timeslot');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to toggle timeslot');
    }
  };

  const handleDeleteTimeslot = async (timeslot: GlobalTimeslotDefinitionResponse) => {
    if (!window.confirm('Are you sure you want to delete this timeslot?')) return;

    try {
      const response = await apiService.deleteGlobalTimeslot(timeslot.id);
      if (response.success) {
        await loadTimeslots();
        onUpdate?.();
      } else {
        setError(response.message || 'Failed to delete timeslot');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete timeslot');
    }
  };

  const handleResetTimeslot = async (timeslot: GlobalTimeslotDefinitionResponse) => {
    if (!window.confirm('Are you sure you want to reset this timeslot to defaults?')) return;

    try {
      const originalStart = formatTimeSpan(timeslot.originalStartTime);
      const originalEnd = formatTimeSpan(timeslot.originalEndTime);
      
      const response = await apiService.resetGlobalTimeslot(originalStart, originalEnd, timeslot.dayOfWeek);
      if (response.success) {
        await loadTimeslots();
        onUpdate?.();
      } else {
        setError(response.message || 'Failed to reset timeslot');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reset timeslot');
    }
  };

  const startEdit = (timeslot: GlobalTimeslotDefinitionResponse, mode: 'time' | 'capacity') => {
    setEditingTimeslot(timeslot);
    setEditMode(mode);
    setEditForm({
      newStartTime: formatTimeSpan(timeslot.effectiveStartTime),
      newEndTime: formatTimeSpan(timeslot.effectiveEndTime),
      newCapacity: timeslot.defaultCapacity,
      reason: ''
    });
  };

  const handleCopyDay = (day: string) => {
    const dayTimeslots = groupedTimeslots[day] || [];
    setCopiedTimeslots(dayTimeslots);
    setCopiedFromDay(day);
    setError('');
    // Show success message briefly
    setSuccessMessage(`Copied ${dayTimeslots.length} timeslots from ${day}`);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handlePaste = () => {
    if (copiedTimeslots.length === 0) {
      setError('No timeslots to paste. Please copy timeslots from a day first.');
      return;
    }
    
    // Auto-fill paste target from current filter
    setPasteTargetDay(selectedDay);
    
    // Reset modal states
    setPasteError('');
    setPasteLoading(false);
    setShowPasteModal(true);
  };

  const handlePasteToDay = async () => {
    if (copiedTimeslots.length === 0) return;

    try {
      setPasteError('');
      setPasteLoading(true);
      const targetDayName = dayNames[pasteTargetDay];
      
      // Delete all existing timeslots for the target day
      const existingTimeslots = timeslots.filter(t => t.dayOfWeek === pasteTargetDay);
      
      // Delete existing timeslots
      for (const timeslot of existingTimeslots) {
        const deleteResponse = await apiService.deleteGlobalTimeslot(timeslot.id);
        if (!deleteResponse.success) {
          throw new Error(`Failed to delete existing timeslot: ${deleteResponse.message}`);
        }
      }

      // Create new timeslots based on copied ones
      for (const copiedTimeslot of copiedTimeslots) {
        const createRequest: CreateGlobalTimeslotRequest = {
          startTime: formatTimeSpan(copiedTimeslot.effectiveStartTime),
          endTime: formatTimeSpan(copiedTimeslot.effectiveEndTime),
          capacity: copiedTimeslot.defaultCapacity,
          dayOfWeek: pasteTargetDay,
          reason: `Copied from ${copiedFromDay}`
        };

        const createResponse = await apiService.createGlobalTimeslot(createRequest);
        if (!createResponse.success) {
          throw new Error(`Failed to create timeslot: ${createResponse.message}`);
        }

        // If the original timeslot was disabled, disable the new one
        if (!copiedTimeslot.isEnabled && createResponse.data) {
          const originalStart = formatTimeSpan(copiedTimeslot.effectiveStartTime);
          const originalEnd = formatTimeSpan(copiedTimeslot.effectiveEndTime);
          
          const disableRequest: UpdateGlobalTimeslotEnabledRequest = {
            isEnabled: false,
            dayOfWeek: pasteTargetDay,
            reason: `Copied disabled state from ${copiedFromDay}`
          };
          
          const disableResponse = await apiService.updateGlobalTimeslotEnabled(originalStart, originalEnd, disableRequest);
          if (!disableResponse.success) {
            throw new Error(`Failed to disable timeslot: ${disableResponse.message}`);
          }
        }
      }

      setShowPasteModal(false);
      await loadTimeslots();
      onUpdate?.();
      
      // Show success message
      setSuccessMessage(`Successfully pasted ${copiedTimeslots.length} timeslots to ${targetDayName}`);
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (err: any) {
      setPasteError(err.message || 'Failed to paste timeslots');
    } finally {
      setPasteLoading(false);
    }
  };

  const filteredTimeslots = timeslots.filter(t => t.dayOfWeek === selectedDay);

  const groupedTimeslots = filteredTimeslots.reduce((acc, timeslot) => {
    const day = timeslot.dayOfWeekName;
    if (!acc[day]) acc[day] = [];
    acc[day].push(timeslot);
    return acc;
  }, {} as Record<string, GlobalTimeslotDefinitionResponse[]>);

  return (
    <div className="global-timeslot-management">
      <div className="global-timeslot-header">
        <h3>{t('globalTimeslotManagement')}</h3>
        <p>{t('filterByDay')}</p>
        
        <div className="controls">
          <select
            value={selectedDay}
            onChange={(e) => setSelectedDay(parseInt(e.target.value) as DayOfWeek)}
            className="day-filter"
          >
            {orderedDays.map((day) => (
              <option key={day} value={day}>{dayNames[day]}</option>
            ))}
          </select>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="create-button"
          >
            {t('createTimeslot')}
          </button>
          
          <button
            onClick={handlePaste}
            className="paste-button"
            disabled={copiedTimeslots.length === 0}
            title={copiedTimeslots.length === 0 ? 'No timeslots copied' : `Paste ${copiedTimeslots.length} timeslots from ${copiedFromDay}`}
          >
            {copiedTimeslots.length === 0 ? 'Paste' : `Paste (${copiedTimeslots.length})`}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      {isLoading ? (
        <div className="loading">Loading timeslots...</div>
      ) : (
        <div className="timeslots-grid" data-single-day="true">
          {Object.entries(groupedTimeslots).map(([day, dayTimeslots]) => (
            <div key={day} className="day-section">
              <div className="day-header">
                <h4>{day}</h4>
                <button
                  onClick={() => handleCopyDay(day)}
                  className="copy-button"
                  title={`Copy ${dayTimeslots.length} timeslots from ${day}`}
                >
                  Copy ({dayTimeslots.length})
                </button>
              </div>
              <div className="timeslots-list">
                {dayTimeslots.map((timeslot) => (
                  <div key={timeslot.id} className={`timeslot-card ${!timeslot.isEnabled ? 'disabled' : ''}`}>
                    <div className="timeslot-header">
                      <div className="time-info">
                        <div className="time-range">
                          {timeslot.originalTimeRange}
                          {timeslot.hasTimeModification && (
                            <span className="effective-time">
                              → {timeslot.effectiveTimeRange}
                            </span>
                          )}
                        </div>
                        <div className="capacity">
                          Capacity: {timeslot.defaultCapacity}
                        </div>
                      </div>
                      
                      <div className="timeslot-status">
                        {!timeslot.isEnabled && <span className="status-badge disabled">Disabled</span>}
                        {timeslot.isModified && <span className="status-badge modified">Modified</span>}
                        {timeslot.isCustomTimeslot && <span className="status-badge custom">Custom</span>}
                      </div>
                    </div>

                    {timeslot.reason && (
                      <div className="reason">
                        <strong>Reason:</strong> {timeslot.reason}
                      </div>
                    )}

                    <div className="timeslot-meta">
                      {timeslot.modifiedByName && (
                        <div>Modified by: {timeslot.modifiedByName}</div>
                      )}
                      <div>Last updated: {new Date(timeslot.modifiedAt).toLocaleDateString()}</div>
                    </div>

                    <div className="timeslot-actions">
                      <button
                        onClick={() => startEdit(timeslot, 'time')}
                        className="edit-button"
                        title="Edit Time"
                      >
                        Edit Time
                      </button>
                      <button
                        onClick={() => startEdit(timeslot, 'capacity')}
                        className="edit-button"
                        title="Edit Capacity"
                      >
                        Edit Capacity
                      </button>
                      <button
                        onClick={() => handleToggleTimeslot(timeslot)}
                        className={`toggle-button ${timeslot.isEnabled ? 'enabled' : 'disabled'}`}
                        title={`${timeslot.isEnabled ? 'Disable' : 'Enable'} timeslot`}
                      >
                        {timeslot.isEnabled ? 'Disable' : 'Enable'}
                      </button>
                      {timeslot.isModified && (
                        <button
                          onClick={() => handleResetTimeslot(timeslot)}
                          className="reset-button"
                          title="Reset to Default"
                        >
                          Reset
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteTimeslot(timeslot)}
                        className="delete-button"
                        title="Delete Timeslot"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Create Global Timeslot</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="close-button"
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="form-group">
                <label>Day of Week:</label>
                <select
                  value={createForm.dayOfWeek}
                  onChange={(e) => setCreateForm({...createForm, dayOfWeek: parseInt(e.target.value) as DayOfWeek})}
                >
                  {orderedDays.map((day) => (
                    <option key={day} value={day}>{dayNames[day]}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Time:</label>
                  <input
                    type="time"
                    value={createForm.startTime}
                    onChange={(e) => setCreateForm({...createForm, startTime: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Time:</label>
                  <input
                    type="time"
                    value={createForm.endTime}
                    onChange={(e) => setCreateForm({...createForm, endTime: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Capacity:</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={createForm.capacity}
                  onChange={(e) => setCreateForm({...createForm, capacity: parseInt(e.target.value)})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Reason (optional):</label>
                <textarea
                  value={createForm.reason}
                  onChange={(e) => setCreateForm({...createForm, reason: e.target.value})}
                  placeholder="Reason for creating this timeslot..."
                />
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={handleCreateTimeslot} className="save-button">
                Create Timeslot
              </button>
              <button onClick={() => setShowCreateModal(false)} className="cancel-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingTimeslot && editMode && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit {editMode === 'time' ? 'Time' : 'Capacity'}</h3>
              <button
                onClick={() => {setEditingTimeslot(null); setEditMode(null);}}
                className="close-button"
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              {editMode === 'time' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>New Start Time:</label>
                      <input
                        type="time"
                        value={editForm.newStartTime}
                        onChange={(e) => setEditForm({...editForm, newStartTime: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>New End Time:</label>
                      <input
                        type="time"
                        value={editForm.newEndTime}
                        onChange={(e) => setEditForm({...editForm, newEndTime: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              {editMode === 'capacity' && (
                <div className="form-group">
                  <label>New Capacity:</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={editForm.newCapacity}
                    onChange={(e) => setEditForm({...editForm, newCapacity: parseInt(e.target.value)})}
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label>Reason:</label>
                <textarea
                  value={editForm.reason}
                  onChange={(e) => setEditForm({...editForm, reason: e.target.value})}
                  placeholder="Reason for this change..."
                  required
                />
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={handleEditTimeslot} className="save-button">
                Save Changes
              </button>
              <button onClick={() => {setEditingTimeslot(null); setEditMode(null);}} className="cancel-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paste Modal */}
      {showPasteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Paste Timeslots</h3>
              <button
                onClick={() => setShowPasteModal(false)}
                className="close-button"
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              {pasteError && (
                <div className="error-message">{pasteError}</div>
              )}
              
              <div className="paste-info">
                <p>You are about to paste <strong>{copiedTimeslots.length} timeslots</strong> from <strong>{copiedFromDay}</strong>.</p>
                <p className="warning">⚠️ This will <strong>replace all existing timeslots</strong> for the selected day.</p>
              </div>

              <div className="form-group">
                <label>Paste to Day:</label>
                <input
                  type="text"
                  value={dayNames[pasteTargetDay]}
                  readOnly
                  className="readonly-input"
                />
                <small className="help-text">Target day is determined by your current filter selection</small>
              </div>

              <div className="timeslots-preview">
                <h4>Timeslots to be pasted:</h4>
                <div className="preview-list">
                  {copiedTimeslots.map((timeslot, index) => (
                    <div key={index} className="preview-item">
                      <span className="time">{formatTimeSpan(timeslot.effectiveStartTime)} - {formatTimeSpan(timeslot.effectiveEndTime)}</span>
                      <span className="capacity">Capacity: {timeslot.defaultCapacity}</span>
                      <span className={`status ${timeslot.isEnabled ? 'enabled' : 'disabled'}`}>
                        {timeslot.isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button 
                onClick={handlePasteToDay} 
                className="save-button"
                disabled={pasteLoading}
              >
                {pasteLoading ? 'Pasting...' : 'Paste Timeslots'}
              </button>
              <button 
                onClick={() => setShowPasteModal(false)} 
                className="cancel-button"
                disabled={pasteLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalTimeslotManagement; 